import React, { useState, useRef, useEffect } from 'react';
import { Pie, PieChart as RechartsChart, Tooltip, Cell, Sector, ResponsiveContainer } from "recharts";
import { TrendingUp, Maximize2, Minimize2 } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../components/ui/card";

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../components/ui/chart";

import { Button } from "../components/ui/button";

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value, chartWidth, chartHeight, isFullScreen }) => {
  const radius = outerRadius + (chartWidth * (isFullScreen ? 0.03 : 0.05)); // Adjust label distance from the pie
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const fontSize = Math.min(chartWidth, chartHeight) * (isFullScreen ? 0.018 : 0.03); // Adjust font size based on chart size and mode

  return (
    <text 
      x={x} 
      y={y} 
      fill="black" 
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      style={{ fontWeight: 'bold', fontSize: `${fontSize}px` }}
    >
      {`${name}: ${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

// Expanded array of English color names
const COLORS = [
  '#FF6B6B', // Coral Red
  '#4ECDC4', // Caribbean Green
  '#45B7D1', // Sky Blue
  '#FFA07A', // Light Salmon
  '#98D8C8', // Seafoam Green
  '#F7DC6F', // Maize Yellow
  '#BB8FCE', // Light Purple
  '#82E0AA', // Light Green
  '#F1948A', // Light Red
  '#85C1E9', // Light Blue
  '#F8C471', // Light Orange
  '#73C6B6', // Light Teal
  '#D7BDE2', // Lavender
  '#FAD7A0', // Peach
  '#A9DFBF', // Mint Green
  '#F5B7B1', // Light Coral
  '#AED6F1', // Pastel Blue
  '#E59866', // Light Brown
  '#A2D9CE', // Aquamarine
  '#D2B4DE', // Light Violet
  '#FADBD8', // Misty Rose
  '#ABEBC6', // Pale Green
  '#F9E79F', // Pale Yellow
  '#D5DBDB', // Light Gray
  '#F4D03F', // Saffron
  '#5DADE2', // Bright Blue
  '#45B39D', // Sea Green
  '#EC7063', // Indian Red
  '#AF7AC5', // Amethyst
  '#5499C7', // Steel Blue
];

const PieChart = ({ data, onElementClick, title }) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [chartSize, setChartSize] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setChartSize({ width, height });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [isFullScreen]);

  if (!data || !data.labels || !data.datasets || data.datasets.length === 0) {
    console.error('Invalid data provided to PieChart:', data);
    return <div>No data available for chart</div>;
  }

  const totalValue = data.datasets[0].data.reduce((sum, value) => sum + value, 0);

  const chartData = data.labels
    .map((label, index) => ({
      name: label,
      value: data.datasets[0].data[index],
      percent: data.datasets[0].data[index] / totalValue,
      fill: COLORS[index % COLORS.length],
    }))
    .filter(item => item.percent > 0); // Filter out zero percent items

  const chartConfig = chartData.reduce((acc, item) => {
    acc[item.name] = {
      label: item.name,
      color: item.fill,
    };
    return acc;
  }, {});

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  const ChartComponent = (
    <ChartContainer
      config={chartConfig}
      className={`pb-0 [&_.recharts-pie-label-text]:fill-foreground ${isFullScreen ? 'w-full h-full' : 'w-full'}`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <RechartsChart>
          <ChartTooltip 
            content={({ payload }) => {
              if (payload && payload.length) {
                const data = payload[0].payload;
                return (
                  <ChartTooltipContent
                    content={[
                      { label: 'Name', value: data.name },
                      { label: 'Value', value: data.value },
                      { label: 'Percentage', value: `${(data.percent * 100).toFixed(2)}%` },
                    ]}
                  />
                );
              }
              return null;
            }}
          />
          <Pie 
            data={chartData} 
            dataKey="value" 
            nameKey="name" 
            cx="50%" 
            cy="50%" 
            outerRadius={Math.min(chartSize.width, chartSize.height) * 0.35}
            label={(props) => renderCustomizedLabel({ ...props, chartWidth: chartSize.width, chartHeight: chartSize.height })}
            labelLine={false}
            onClick={(entry, index) => onElementClick(index)}
            animationDuration={300}
            animationBegin={0}
          >
            {
              chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))
            }
          </Pie>
        </RechartsChart>
      </ResponsiveContainer>
    </ChartContainer>
  );

  const chartContent = (
    <div ref={containerRef} className={`w-full ${isFullScreen ? 'h-screen' : 'h-[500px]'}`}>
      {ChartComponent}
    </div>
  );

  if (isFullScreen) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col items-center justify-center">
        <div className="absolute top-4 left-4 z-10 text-left">
          <h2 className="text-2xl font-bold">{title}</h2>
        </div>
        <Button 
          onClick={toggleFullScreen} 
          className="absolute top-4 right-4 z-10"
          variant="outline"
        >
          <Minimize2 className="mr-2 h-4 w-4" /> Exit Full Screen
        </Button>
        {chartContent}
      </div>
    );
  }

  return (
    <Card className="flex flex-col items-center w-full max-w-4xl mx-auto relative">
      <CardHeader className="w-full text-center pb-0">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <Button 
        onClick={toggleFullScreen} 
        className="absolute top-4 right-4 z-10"
        variant="outline"
        size="sm"
      >
        <Maximize2 className="h-4 w-4" />
      </Button>
      <CardContent className="w-full flex justify-center pb-0">
        {chartContent}
      </CardContent>
    </Card>
  );
};

export default PieChart;
